import {
	ChatParticipantType,
	type ChatChannelType,
	type ChatConversationStatus,
	type ChatConversationVisibility,
	type ChatParticipantRole,
} from '@supply-mover-2-0/shared';

/**
 * Client-side view models. We deliberately mirror the server entities
 * but use camelCase and drop columns the UI doesn't need.
 */

/**
 * Parsed shape of a chat principalId. The wire format is
 * `<type>:<tenantAlias>:<workdUserId>:<scopeId>` where scopeId is the
 * portal user's `public_user._id` for portal principals and the
 * internal user's `_user._id` (= instanceUserId) for internals.
 *
 * Centralizing the parse here is the antidote to the workd-vs-instance
 * id confusion: every consumer reads the same fields with the same
 * names. In a tenant DB, `fkUserId`-style FKs reference `_user._id`
 * (instanceUserId), while audit columns like `_creator_userId`
 * reference workdUserId — so any "is this me?" comparison against a
 * tenant FK should use `instanceUserId`.
 */
export interface ParsedPrincipalId {
	raw: string;
	type: 'internal' | 'portal' | 'service';
	tenantAlias: string;
	workdUserId: number;
	/** Internal: `_user._id`. Portal: `public_user._id`. */
	scopeId: number;
	/** Convenience: instanceUserId for internal principals only. */
	instanceUserId: number | null;
	/** Convenience: publicUserId for portal principals only. */
	publicUserId: number | null;
	/** Maps to ChatParticipantType for participant lookups. */
	participantType: ChatParticipantType;
}

export function parsePrincipalId(
	id: string | null | undefined,
): ParsedPrincipalId | null {
	if (id == null || id === '') return null;
	const segs = id.split(':');
	if (segs.length !== 4) return null;
	const [typeStr, tenantAlias, workdRaw, scopeRaw] = segs;
	// Unreachable: length === 4 is checked above
	if (tenantAlias == null) return null;
	if (typeStr !== 'internal' && typeStr !== 'portal' && typeStr !== 'service') {
		return null;
	}
	const workdUserId = Number(workdRaw);
	const scopeId = Number(scopeRaw);
	if (!Number.isFinite(workdUserId) || !Number.isFinite(scopeId)) {
		return null;
	}
	const isPortal = typeStr === 'portal';
	return {
		raw: id,
		type: typeStr,
		tenantAlias,
		workdUserId,
		scopeId,
		instanceUserId: !isPortal && typeStr !== 'service' ? scopeId : null,
		publicUserId: isPortal ? scopeId : null,
		participantType: isPortal
			? ChatParticipantType.PortalUser
			: ChatParticipantType.InternalUser,
	};
}

/**
 * Identity-based "is this message from me?" check. Compares the
 * message's denormalized senderWorkdUserId against the current
 * principal's workdUserId — a single numeric compare with no
 * dependency on the conversation's participants list having loaded.
 *
 * Why this is its own helper: alignment used to be derived from
 * `senderParticipantId === myParticipant.id`, which required the
 * participants REST fetch to land before any message could render
 * on the correct side. On a freshly-opened conversation, the first
 * send raced that fetch and the optimistic bubble landed left-of-
 * center until refresh. Identity comparison via workdUserId removes
 * the dependency entirely.
 */
export function isMessageMine(
	msg: Pick<ChatMessageView, 'senderWorkdUserId'>,
	principalId: string | null,
): boolean {
	if (msg.senderWorkdUserId == null) return false;
	const me = parsePrincipalId(principalId);
	if (me == null) return false;
	return msg.senderWorkdUserId === me.workdUserId;
}

export interface ChatConversationView {
	id: number;
	uuid: string;
	channel: ChatChannelType;
	name?: string;
	topic?: string;
	status: ChatConversationStatus;
	visibility: ChatConversationVisibility;
	linkedClientId?: number;
	// Server-enriched for customer-support rows so the CRM sidebar and
	// header can render the client's company name rather than `#<id>`.
	linkedClientName?: string;
	assignedRepUserId?: number;
	latestMessageId?: number;
	lastMessageAt?: string;
	messageCount: number;
}

export interface ChatVisibilityGates {
	visibility: ChatConversationVisibility;
	teamIds: Array<number>;
	rbacRoleIds: Array<number>;
}

export interface ChatParticipantView {
	id: number;
	conversationId: number;
	participantType: ChatParticipantType;
	userId?: number;
	externalIdentifier?: string;
	role: ChatParticipantRole;
	/** Server-enriched — firstName lastName, fallback email, fallback #id. */
	displayName: string;
	email?: string;
	isActive: boolean;
	notificationsMuted: boolean;
}

export interface ChatMessageView {
	id: number;
	uuid: string;
	conversationId: number;
	/**
	 * FK to the conversation-scoped chat_participant row of the sender.
	 * Used for display-name lookup of OTHER senders via the conversation's
	 * participants list. NOT used for the "is this mine?" decision —
	 * that's `senderWorkdUserId`. Undefined briefly on optimistic rows
	 * (filled in when the server fanout arrives and replaces the
	 * optimistic via clientMessageId dedup) and null for system messages.
	 */
	senderParticipantId?: number;
	/**
	 * Identity primitive — the sender's `workdUserId`. Spans `_user` and
	 * `public_user` (both carry workdUserId), so a single numeric compare
	 * against the current principal's workdUserId tells us whether the
	 * message is ours, regardless of whether the sender or current user
	 * is internal or portal. Set at every entry point (optimistic send,
	 * MQTT fanout, history REST), so alignment never has to wait on the
	 * participants list. Null only for server-emitted system messages.
	 */
	senderWorkdUserId?: number;
	/** Outgoing-only — lets us optimistically render before server confirms. */
	clientMessageId?: string;
	kind: 'text' | 'file' | 'system' | 'call_event';
	content?: string;
	parentMessageId?: number;
	editedAt?: string;
	softDeletedAt?: string;
	reactions?: Record<string, Array<number>>;
	/** Matches the server entity's CreateDateColumn (BaseEntity.createdDate). */
	createdDate: string;
	/**
	 * FK into `_file` for any attached file. The client never uses
	 * the key/bucket directly — it calls
	 * `ChatQueryService.getAttachmentDownloadUrl(messageId)` to mint
	 * a participation-gated presigned URL on demand. `fileName` and
	 * `fileContentType` come down via the MQTT fanout (or the history
	 * endpoint) so the bubble can render before the URL resolves.
	 */
	fkFileId?: number;
	fileName?: string;
	fileContentType?: string;
	/**
	 * Free-form metadata block. Today's known consumers:
	 *
	 *   - `metadata.callEvent` — populated by
	 *     `ChatCallEventWriterService` (chat module) on
	 *     `kind='call_event'` rows. Carries direction / duration /
	 *     handler info / recording flag for the inbox bubble's
	 *     call-card variant.
	 *   - `metadata.transfer` — populated by
	 *     `ChatConversationTransferService` on the audit
	 *     `kind='system'` rows it posts when a conversation is
	 *     transferred (rep → rep / rep → AI / AI → human / AI → AI).
	 *   - Inbound SMS rows carry the Twilio provider data
	 *     (`metadata.providerData`, etc.) — surfaced for diagnostic
	 *     tooling, not the bubble view.
	 *
	 * Untyped at this seam because the schema is per-kind. Consumers
	 * narrow on `kind` first then assert the matching shape.
	 */
	metadata?: Record<string, unknown>;
}

export interface ChatIdentityResponse {
	principalId: string;
	principalType: 'internal' | 'portal' | 'service';
	inboxTopic: string;
	outboxTopic: string;
	presenceTopic: string;
	/**
	 * Internal-only tenant-scoped event topics. Undefined for portal
	 * principals. The client must NOT reconstruct these locally — the
	 * server's `ChatTopicService` is the single source of truth so the
	 * env segment matches the authorizer's grant strings exactly.
	 */
	queueEventsTopic?: string;
	availabilityEventsTopic?: string;
	brokerUrl: string;
}

export interface ChatQueueEntryView {
	id: number;
	conversationId: number;
	clientId: number;
	portalUserId: number;
	status: 'waiting' | 'assigned' | 'abandoned' | 'resolved';
	routingMode: string;
	requestedRepUserId?: number;
	assignedToUserId?: number;
	priority: number;
	enqueuedAt: string;
	topic?: string;
	/**
	 * Server-enriched display fields. The queue controller batch-
	 * loads ClientEntity + PublicUserEntity for every entry so the
	 * rep's queue card can render "Acme Corp · Jane Doe" without an
	 * N+1 fetch loop on the client.
	 */
	clientName?: string | null;
	portalUserName?: string | null;
}

/**
 * How much of a conversation's history travels with the people being
 * added. Mirrors the choice Teams offers.
 */
export type ChatHistoryWindow = 'none' | 'last-7-days' | 'last-30-days' | 'all';

export interface ChatShareHistoryResult {
	/** The room to show now — a new channel, or the converted room itself. */
	conversationId: number;
	/** True when the source room BECAME the channel rather than being copied. */
	converted: boolean;
	messagesCopied: number;
}
